import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast.tsx';
import { Modal } from '../components/Modal';
import type { Utilizador, CreateUtilizadorRequest, UpdateUtilizadorRequest } from '../types';
import { utilizadoresApi, rolesApi, RoleDto } from '../services/api';
import { Plus, Edit, Trash2, UserCog } from 'lucide-react';

interface UtilizadorForm {
  nome: string;
  password: string;
  role: string; // role id as string (dinâmico)
  ativo: boolean;
}

export function UtilizadoresPage() {
  const { success, error } = useToast();
  const { user } = useAuth();
  const [utilizadores, setUtilizadores] = useState<Utilizador[]>([]);
  const [roles, setRoles] = useState<RoleDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState<Utilizador | null>(null);
  const [form, setForm] = useState<UtilizadorForm>({ nome: '', password: '', role: '2', ativo: true });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const roleNome = (roleVal: string) => {
    // role pode ser "1", "Admin", "2" etc — tenta mapear via lista roles
    const r = roles.find(x => String(x.id) === String(roleVal) || x.nome === roleVal);
    return r ? r.nome : roleVal;
  };

  const loadUtilizadores = useCallback(async () => {
    try {
      setLoading(true);
      const response = await utilizadoresApi.getAll();
      if (response.success && response.data) setUtilizadores(response.data);
    } catch (err) {
      console.error('Failed to load utilizadores:', err);
      error('Erro ao carregar utilizadores');
    }
    finally { setLoading(false); }
  }, [error]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const response = await utilizadoresApi.getAll();
        if (active && response.success && response.data) setUtilizadores(response.data);
      } catch (err) {
        if (active) {
          console.error('Failed to load utilizadores:', err);
          error('Erro ao carregar utilizadores');
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    (async () => {
      try {
        const r = await rolesApi.getAll();
        if (!active || !r.success || !r.data) return;
        setRoles(r.data);
        if (r.data.length) setForm(f => f.role ? f : { ...f, role: String(r.data![0].id) });
      } catch (err) {
        // ignora - roles são opcionais para o form funcionar
        if (active) console.error('Failed to load roles:', err);
      }
    })();
    return () => { active = false; };
  }, [error]);

  const openNovo = () => {
    const defaultRole = roles.length ? String(roles.find(x=>x.nome==='Staff')?.id ?? roles[0].id) : '2';
    setForm({ nome: '', password: '', role: defaultRole, ativo: true });
    setEditando(null); setFormError(''); setModalOpen(true);
  };
  const openEditar = (u: Utilizador) => {
    // u.role vem como string (ex "1" ou "Admin") — normaliza para id
    let roleId = String(u.role);
    const found = roles.find(x => x.nome === u.role || String(x.id) === String(u.role));
    if (found) roleId = String(found.id);
    else if (u.role === 'Admin') roleId = '1';
    else if (u.role === 'Staff') roleId = '2';
    else if (u.role === 'SuperAdmin') roleId = '0';
    setForm({ nome: u.nome, password: '', role: roleId, ativo: u.ativo });
    setEditando(u); setFormError(''); setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nome.trim()) { setFormError('O nome é obrigatório'); return; }
    setSaving(true);
    try {
      if (editando) {
        const req: UpdateUtilizadorRequest = { nome: form.nome.trim(), password: form.password.trim() || undefined, role: form.role, ativo: form.ativo, versao: editando.versao || 1 };
        const r = await utilizadoresApi.update(editando.id, req);
        if (r.success){ success('Utilizador atualizado'); setModalOpen(false); loadUtilizadores(); } else setFormError(r.message || 'Erro');
      } else {
        if (!form.password){ setFormError('A palavra-passe é obrigatória'); setSaving(false); return; }
        const req: CreateUtilizadorRequest = { nome: form.nome.trim(), password: form.password, role: form.role, ativo: form.ativo };
        const r = await utilizadoresApi.create(req);
        if (r.success){ success('Utilizador criado'); setModalOpen(false); loadUtilizadores(); } else setFormError(r.message || 'Erro');
      }
    } catch (err: any){ setFormError(err.message ?? 'Erro'); }
    finally { setSaving(false); }
  };

  const handleEliminar = async (u: Utilizador) => {
    if (user?.id === u.id) { error('Não pode eliminar a sua própria conta'); return; }
    if (!confirm(`Eliminar "${u.nome}"?`)) return;
    try {
      const r = await utilizadoresApi.delete(u.id);
      if (r.success){ success('Eliminado'); loadUtilizadores(); } else error(r.message || 'Erro');
    } catch { error('Erro ao eliminar'); }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Utilizadores</h1>
        <div className="header-actions">
          <a href="#/roles" onClick={(e)=>{e.preventDefault(); window.location.hash='#/roles'; window.location.pathname='/roles'; window.history.pushState({},'','/roles'); window.dispatchEvent(new PopStateEvent('popstate'));}} className="btn btn-secondary" style={{marginRight:8}}>Gerir Roles</a>
          <button className="btn btn-primary" onClick={openNovo}><Plus className="w-4 h-4"/> Novo Utilizador</button>
        </div>
      </div>
      <div className="card">
        {loading ? <div className="page-loading"><div className="spinner"></div><p>A carregar...</p></div> : (
          <div className="table-container">
            <table className="table"><thead><tr><th>Nome</th><th>Role</th><th>Estado</th><th>Último Login</th><th>Ações</th></tr></thead>
              <tbody>{utilizadores.map(u=>(
                <tr key={u.id}><td>{u.nome} {user?.id===u.id && <span className="badge badge-info ml-2">Você</span>}</td>
                  <td><span className="badge badge-neutral">{roleNome(String(u.role))}</span></td>
                  <td><span className={`badge ${u.ativo?'badge-success':'badge-danger'}`}>{u.ativo?'Ativo':'Inativo'}</span></td>
                  <td>{u.ultimoLogin ? new Date(u.ultimoLogin).toLocaleString('pt-PT') : 'Nunca'}</td>
                  <td><div className="table-actions">
                    <button className="btn btn-ghost btn-sm btn-icon" onClick={()=>openEditar(u)}><Edit className="w-4 h-4"/></button>
                    <button className="btn btn-ghost btn-sm btn-icon text-red-600" onClick={()=>handleEliminar(u)}><Trash2 className="w-4 h-4"/></button>
                  </div></td>
                </tr>))}</tbody>
            </table>
          </div>
        )}
        {!loading && utilizadores.length===0 && <div className="empty-state"><UserCog className="w-12 h-12 text-gray-300"/><h3>Nenhum utilizador</h3><button className="btn btn-primary mt-4" onClick={openNovo}><Plus className="w-4 h-4"/> Novo</button></div>}
      </div>
      <Modal title={editando?'Editar Utilizador':'Novo Utilizador'} open={modalOpen} onClose={()=>setModalOpen(false)} footer={
        <><button className="btn btn-secondary" onClick={()=>setModalOpen(false)}>Cancelar</button><button type="submit" form="utilizador-form" className="btn btn-primary" disabled={saving}>{saving?'A guardar...': editando?'Atualizar':'Criar'}</button></>
      }>
        <form id="utilizador-form" onSubmit={handleSubmit} noValidate>
          {formError && <div className="form-error" role="alert">{formError}</div>}
          <div className="form-group"><label className="label">Nome *</label><input className="input" value={form.nome} onChange={e=>setForm(p=>({...p,nome:e.target.value}))} required /></div>
          <div className="form-group"><label className="label">Palavra-passe {!editando && <span className="required">*</span>}</label><input type="password" className="input" value={form.password} onChange={e=>setForm(p=>({...p,password:e.target.value}))} placeholder={editando?'Deixar em branco para manter':'Palavra-passe'} autoComplete="new-password" />{editando && <span className="form-hint">Deixe em branco para manter</span>}</div>
          <div className="form-row">
            <div className="form-group"><label className="label">Role</label>
              <select className="input" value={form.role} onChange={e=>setForm(p=>({...p,role:e.target.value}))}>
                {roles.map(r=> <option key={r.id} value={String(r.id)}>{r.nome} {r.id<=2?`(${r.id})`:''}</option>)}
                {roles.length===0 && <><option value="0">SuperAdmin</option><option value="1">Admin</option><option value="2">Staff</option></>}
              </select>
              <span className="form-hint">Permissões são definidas em Roles → {form.role && roles.find(x=>String(x.id)===form.role)?.nome}</span>
            </div>
            <div className="form-group form-checkbox"><label className="checkbox-label"><input type="checkbox" checked={form.ativo} onChange={e=>setForm(p=>({...p,ativo:e.target.checked}))}/><span>Ativo</span></label></div>
          </div>
        </form>
      </Modal>
    </div>
  );
}
